import {
  IComplaintDashboardRankItem,
  IComplaintDashboardSummary,
  IComplaintDashboardSummaryRequest,
  IComplaintDashboardTrendItem,
  IComplaintDashboardTrendRequest,
  ComplaintsDashboardDimensionKey,
  IComplaintsDashboardFilters,
  IComplaintsDashboardOptions,
  IComplaintsDashboardTrendView,
  IComplaintsDashboardView,
  IDashboardDateRange,
  IDashboardKpi,
  IDashboardRankRow,
  DashboardTone,
  IDashboardTrendMonth,
  IDashboardTypeBar,
  IRangeOption,
} from '../../../core/interfaces/complaints-dashboard.interface';
import { CleanHelper } from '../../../core/helpers/clean-helper';

export const COMPLAINT_RANGE_OPTIONS: IRangeOption[] = [
  { v: '30', l: 'Last 30 Days' },
  { v: '90', l: 'Last 90 Days' },
  { v: '180', l: 'Last 180 Days' },
  { v: '365', l: 'Last 12 Months' },
  { v: 'custom', l: 'Custom' },
];

export const DEFAULT_DASHBOARD_OPTIONS: IComplaintsDashboardOptions = {
  repeatThreshold: 3,
  pendingAgingDays: 14,
  showPriorDelta: true,
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const DEFAULT_RANGE_DAYS = 30;
export const MAX_CUSTOM_RANGE_DAYS = 365;
const DAY_MS = 864e5;
export const INVALID_DATE_MESSAGE = 'Enter a valid date.';
const DASH = '—';

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function formatLocalDate(date: Date): string {
  return (
    date.getFullYear() +
    '-' +
    pad2(date.getMonth() + 1) +
    '-' +
    pad2(date.getDate())
  );
}

export function createDefaultFilters(today: Date): IComplaintsDashboardFilters {
  return {
    payor: '',
    range: String(DEFAULT_RANGE_DAYS),
    from: formatLocalDate(addDays(today, 1 - DEFAULT_RANGE_DAYS)),
    to: formatLocalDate(today),
    provider: '',
    area: '',
    type: '',
    status: '',
    review: '',
  };
}

export function clearDimensionFilters(
  filters: IComplaintsDashboardFilters,
): IComplaintsDashboardFilters {
  return Object.assign({}, filters, {
    payor: '',
    provider: '',
    area: '',
    type: '',
    status: '',
    review: '',
  });
}

export function toggleFilter(
  filters: IComplaintsDashboardFilters,
  key: ComplaintsDashboardDimensionKey,
  value: string,
): IComplaintsDashboardFilters {
  return Object.assign({}, filters, {
    [key]: filters[key] === value ? '' : value,
  });
}

export function resolveDateRange(
  filters: IComplaintsDashboardFilters,
  today: Date,
): IDashboardDateRange {
  if (filters.range === 'custom') {
    const from =
      filters.from || formatLocalDate(addDays(today, 1 - DEFAULT_RANGE_DAYS));
    const to = filters.to || formatLocalDate(today);
    return from > to
      ? { startDate: to, endDate: from }
      : { startDate: from, endDate: to };
  }
  return {
    startDate: formatLocalDate(
      addDays(today, 1 - (+filters.range || DEFAULT_RANGE_DAYS)),
    ),
    endDate: formatLocalDate(today),
  };
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return false;
  }
  const p = value.split('-').map(Number);
  const date = new Date(p[0], p[1] - 1, p[2]);
  return (
    date.getFullYear() === p[0] &&
    date.getMonth() === p[1] - 1 &&
    date.getDate() === p[2]
  );
}

export function validateCustomRange(
  from: string,
  to: string,
  today: string,
): string {
  if (!isIsoDate(from) || !isIsoDate(to)) {
    return INVALID_DATE_MESSAGE;
  }
  if (to > today) {
    return 'The end date cannot be after today.';
  }
  if (from > to) {
    return 'The start date must be on or before the end date.';
  }
  if (dayOf(to) - dayOf(from) + 1 > MAX_CUSTOM_RANGE_DAYS) {
    return 'The range cannot exceed ' + MAX_CUSTOM_RANGE_DAYS + ' days.';
  }
  return '';
}

// While a year is typed, the date input reports values such as '0202-08-01'.
export function isPartialDate(value: string): boolean {
  return !value || /^0\d{3}-/.test(value);
}

export function buildTrendParams(
  filters: IComplaintsDashboardFilters,
): IComplaintDashboardTrendRequest {
  const params: IComplaintDashboardTrendRequest = {};
  if (filters.payor) {
    params.payorId = +filters.payor;
  }
  if (filters.provider) {
    params.providerId = +filters.provider;
  }
  if (filters.area) {
    params.serviceAreaId = +filters.area;
  }
  if (filters.type) {
    params.complaintTypeId = +filters.type;
  }
  if (filters.status) {
    params.statusId = +filters.status;
  }
  if (filters.review) {
    params.reviewStatusId = +filters.review;
  }
  return params;
}

export function buildSummaryParams(
  filters: IComplaintsDashboardFilters,
  options: IComplaintsDashboardOptions,
  today: Date,
): IComplaintDashboardSummaryRequest {
  const range = resolveDateRange(filters, today);
  const opts: IComplaintsDashboardOptions = Object.assign(
    {},
    DEFAULT_DASHBOARD_OPTIONS,
    options,
  );
  return Object.assign(
    {
      startDate: range.startDate,
      endDate: range.endDate,
      repeatThreshold: opts.repeatThreshold,
      pendingAgingDays: opts.pendingAgingDays,
    },
    buildTrendParams(filters),
  );
}

function dayOf(str: string): number {
  const p = str.slice(0, 10).split('-').map(Number);
  return Date.UTC(p[0], p[1] - 1, p[2]) / DAY_MS;
}

function formatDay(str: string): string {
  const p = str.slice(0, 10).split('-');
  return p[1] + '/' + p[2] + '/' + p[0];
}

function formatInt(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function rateOf(n: number, t: number): number {
  return t ? (n / t) * 100 : 0;
}

function rateFmt(n: number, t: number): string {
  return t ? rateOf(n, t).toFixed(2) + '%' : DASH;
}

function validPct(validated: number, total: number): string {
  return total ? Math.round((validated / total) * 100) + '%' : DASH;
}

function idOf(id: number): string {
  return CleanHelper.hasValue(id) ? String(id) : '';
}

export function buildDashboard(
  summary: IComplaintDashboardSummary,
  filters: IComplaintsDashboardFilters,
  options?: IComplaintsDashboardOptions,
): IComplaintsDashboardView {
  const opts: IComplaintsDashboardOptions = Object.assign(
    {},
    DEFAULT_DASHBOARD_OPTIONS,
    options,
  );
  const repeatMin = opts.repeatThreshold;
  const agingDays = opts.pendingAgingDays;
  const showDelta = opts.showPriorDelta;
  const k = summary.kpis;

  const startDay = dayOf(summary.range.start),
    endDay = dayOf(summary.range.end);
  const rangeLabel =
    formatDay(summary.range.start) + ' – ' + formatDay(summary.range.end);
  const priorLabel =
    'vs prior ' +
    (filters.range === '365' ? '12 months' : endDay - startDay + 1 + ' days');

  const delta = (
    cv: number,
    pv: number,
  ): { label: string; tone: DashboardTone } => {
    if (!showDelta) {
      return { label: '', tone: 'neutral' };
    }
    if (!pv) {
      return { label: DASH, tone: 'neutral' };
    }
    const p = Math.round(((cv - pv) / pv) * 100);
    return {
      label: (p > 0 ? '+' : '') + p + '%',
      tone: p > 0 ? 'bad' : p < 0 ? 'good' : 'neutral',
    };
  };

  const total = k.totalcomplaints;
  const dCount = delta(total, k.priortotalcomplaints);
  const dRate = delta(
    rateOf(total, k.completedtrips),
    rateOf(k.priortotalcomplaints, k.priorcompletedtrips),
  );
  const kpis: IDashboardKpi[] = [
    {
      label: 'Total Complaints',
      value: formatInt(total),
      sub: showDelta ? dCount.label + ' ' + priorLabel : rangeLabel,
      subTone: dCount.tone,
    },
    {
      label: 'Complaint Rate',
      value: rateFmt(total, k.completedtrips),
      sub: showDelta ? dRate.label + ' ' + priorLabel : 'of completed trips',
      subTone: dRate.tone,
    },
    {
      label: 'Completed Trips',
      value: formatInt(k.completedtrips),
      sub:
        '1 complaint per ' +
        (total ? formatInt(k.completedtrips / total) : DASH) +
        ' trips',
      subTone: 'neutral',
    },
    {
      label: 'Pending',
      value: String(k.pending),
      sub: k.pendingaged + ' older than ' + agingDays + ' days',
      subTone: k.pendingaged ? 'bad' : 'neutral',
    },
    {
      label: 'Validated',
      value: validPct(k.validated, total),
      sub:
        k.invalidated +
        ' invalidated · ' +
        k.insufficientinfo +
        ' insufficient info',
      subTone: 'neutral',
    },
    {
      label: 'Avg Days to Resolve',
      value: CleanHelper.hasValue(k.avgdaystoresolve)
        ? k.avgdaystoresolve.toFixed(1)
        : DASH,
      sub:
        (CleanHelper.hasValue(k.resolvedwithin7dayspct)
          ? k.resolvedwithin7dayspct + '%'
          : 'N/A') + ' resolved within 7 days',
      subTone: 'neutral',
    },
  ];

  const typeMax = Math.max(1, ...summary.bytype.map((t) => t.count));
  const typeBars: IDashboardTypeBar[] = summary.bytype
    .map((t) => ({
      id: idOf(t.id),
      label: t.name,
      count: t.count,
      pct: total ? Math.round((t.count / total) * 100) + '%' : '',
      w: Math.round((t.count / typeMax) * 100) + '%',
      selected: filters.type === idOf(t.id),
    }))
    .sort((x, y) => y.count - x.count);

  const rankRows = (
    items: IComplaintDashboardRankItem[],
    selectedId: string,
  ): IDashboardRankRow[] =>
    items
      .map((item) => {
        const id = idOf(item.id);
        const rc = rateOf(item.complaints, item.completedtrips);
        const d = delta(
          rc,
          rateOf(item.priorcomplaints, item.priorcompletedtrips),
        );
        const row: IDashboardRankRow = {
          id,
          label: item.name,
          trips: formatInt(item.completedtrips),
          total: item.complaints,
          rate: rateFmt(item.complaints, item.completedtrips),
          pending: item.pending,
          valid: validPct(item.validated, item.complaints),
          delta: d.label,
          dTone: d.tone,
          selected: !!id && selectedId === id,
        };
        return { sort: rc, row };
      })
      .sort((x, y) => y.sort - x.sort)
      .map((entry) => entry.row);

  return {
    rangeLabel,
    kpis,
    typeBars,
    provRows: rankRows(summary.byprovider, filters.provider),
    areaRows: rankRows(summary.byservicearea, filters.area),
    repeatRows: summary.repeatmembers.map((r) => ({
      label: r.name,
      dob: r.dob ? formatDay(r.dob) : DASH,
      total: r.complaints,
      top: r.toptype,
    })),
    repeatLabel: repeatMin + '+ complaints',
  };
}

export function buildTrend(
  items: IComplaintDashboardTrendItem[],
  range: IDashboardDateRange,
): IComplaintsDashboardTrendView {
  const startDay = dayOf(range.startDate),
    endDay = dayOf(range.endDate);
  const cMax = Math.max(1, ...items.map((m) => m.complaints));
  const rates = items.map((m) => rateOf(m.complaints, m.completedtrips));
  const rMin = Math.min(...rates),
    rMax = Math.max(...rates),
    rSpan = rMax - rMin || 1;
  const plotPct = (units: number) => ((units / 170) * 100).toFixed(2) + '%';
  const months: IDashboardTrendMonth[] = items.map((m, i) => {
    const y = Math.round(52 + ((rates[i] - rMin) / rSpan) * 96);
    const monthStart = Date.UTC(m.year, m.month - 1, 1) / DAY_MS,
      monthEnd = Date.UTC(m.year, m.month, 0) / DAY_MS;
    return {
      label: `${MONTHS[m.month - 1]} '${String(m.year).slice(2)}`,
      count: m.complaints,
      trips: formatInt(m.completedtrips),
      rate: rateFmt(m.complaints, m.completedtrips),
      h: plotPct((m.complaints / cMax) * 120),
      active: monthStart <= endDay && monthEnd >= startDay,
      x: (((i + 0.5) / 12) * 100).toFixed(3) + '%',
      y: plotPct(y),
      ly: plotPct(y + 9),
      py: 170 - y,
    };
  });
  const ratePts = months.map((m, i) => (i + 0.5) * 100 + ',' + m.py).join(' ');
  return { months, ratePts };
}



// WEBPACK FOOTER //
// ./src/app/pages/complaints/complaints-dashboard/complaints-dashboard.helper.ts